import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import React, { useMemo } from 'react';
import { ArrowLeft, Battery, Boxes, ExternalLink, MapPin, Radio, ShieldCheck, Smartphone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { getNodeBattery } from '../../lib/liveSensorMetrics';
import { isNodeOnline, nodeTimestamp } from '../../lib/operationalOverview';
import { equipmentType, formatEquipmentValue } from '../adapters/equipmentPresentation';
import { useAgriSafeSoverdiDashboard } from '../hooks/useAgriSafeSoverdiDashboard';

const ageText = (timestamp: number | null): string => {
  if (timestamp === null) return i18n.t("sectorViews.Non_remontee");
  const seconds = Math.max(0, Math.floor((Date.now() - timestamp) / 1000));
  const relative = new Intl.RelativeTimeFormat(i18n.language, { numeric: 'always' });
  if (seconds < 60) return relative.format(-seconds, 'second');
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return relative.format(-minutes, 'minute');
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return relative.format(-hours, 'hour');
  return relative.format(-Math.floor(hours / 24), 'day');
};

const SoverdiEquipmentSettingsR7: React.FC = () => {
  useTranslation();
  const navigate = useNavigate();
  const { topology, nodes, nodesLoaded, placement, sectors, parcelBindings, parcelDisplayName, pressureSensorByBinding, siteModels, loading, loadError } = useAgriSafeSoverdiDashboard();

  const sectorDisplayNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const sector of sectors) {
      const key = `${sector.siteName}::${sector.sectorCode}`;
      map.set(key, [map.get(key), sector.displayName].filter(Boolean).join(' · '));
    }
    return map;
  }, [sectors]);

  const nodeLocation = useMemo(() => {
    const map = new Map<string, { siteName: string; sectorDisplayName?: string }>();
    if (!nodesLoaded) return map;

    for (const node of nodes) {
      if (!node.sector_id) continue;
      const located = placement.get(node.sector_id);
      if (located) {
        map.set(node.id, {
          siteName: located.siteName,
          sectorDisplayName: sectorDisplayNames.get(`${located.siteName}::${located.sectorCode}`),
        });
      }
    }

    for (const node of nodes) {
      const assigned = (parcelBindings ?? []).filter(binding => binding.controller_node_id === node.id || pressureSensorByBinding?.[binding.binding_id] === node.id);
      if (assigned.length) map.set(node.id, { siteName: assigned[0].site,
        sectorDisplayName: assigned.map(binding => parcelDisplayName?.(binding) ?? binding.parcel).join(' · ') });
    }

    for (const site of siteModels) {
      if (site.relayNode) map.set(site.relayNode.id, { siteName: site.siteName });
    }

    return map;
  }, [nodes, nodesLoaded, placement, sectorDisplayNames, siteModels, parcelBindings, parcelDisplayName, pressureSensorByBinding]);

  const onlineCount = useMemo(
    () => nodesLoaded ? nodes.filter((node) => isNodeOnline(node, Date.now())).length : null,
    [nodes, nodesLoaded],
  );

  const sitesWithEquipment = useMemo(
    () => nodesLoaded ? new Set(Array.from(nodeLocation.values()).map((location) => location.siteName)).size : null,
    [nodeLocation, nodesLoaded],
  );

  const nonRecentCount = onlineCount === null ? null : Math.max(0, nodes.length - onlineCount);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="equipment-settings-loading">
        <div className="w-10 h-10 rounded-full border-2 border-[var(--color-primary)] border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!topology) {
    return (
      <div className="space-y-4" data-testid="equipment-settings-unavailable">
        <button type="button" onClick={() => navigate('/r6-soverdi-settings')} className="inline-flex items-center gap-2 text-sm font-medium text-[var(--color-primary)]">
          <ArrowLeft className="h-4 w-4" /> {i18n.t("settings")}</button>
        <div className="rounded-xl border border-[var(--border-light)] bg-[var(--surface)] p-6 text-sm text-[var(--text-secondary)]">
          {i18n.t("sectorViews.Les_affectations_d_equipements_sont_indisponibles_Aucun_inventaire_fic")}</div>
      </div>
    );
  }

  return (
    <div className="space-y-5 sm:space-y-6" data-testid="equipment-settings-r7">
      <section className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <button type="button" onClick={() => navigate('/r6-soverdi-settings')} className="inline-flex items-center gap-2 text-sm font-medium text-[var(--color-primary)]">
            <ArrowLeft className="h-4 w-4" /> {i18n.t("settings")}</button>
          <div className="mt-4 flex items-center gap-2 text-[var(--color-primary)]">
            <Boxes className="h-4 w-4" />
            <span className="text-xs font-semibold uppercase tracking-wide">{i18n.t("exploitation")}</span>
          </div>
          <h2 className="mt-1 text-xl font-semibold text-[var(--text-primary)] sm:text-2xl">{i18n.t("sectorViews.Equipements_affectations")}</h2>
          <p className="mt-1 max-w-3xl text-sm text-[var(--text-secondary)]">
            {i18n.t("sectorViews.Consultez_les_equipements_rattaches_a")} {topology.clientName} {i18n.t("sectorViews.et_leur_affectation_metier_Les_identifiants_techniques_et_le_commissio")}</p>
        </div>
        <span className="inline-flex w-fit items-center gap-1.5 rounded-full border border-[var(--border-light)] bg-[var(--surface)] px-3 py-1.5 text-xs font-medium text-[var(--text-secondary)]">
          <ShieldCheck className="h-3.5 w-3.5" /> {i18n.t("readOnlyShort")}</span>
      </section>

      {(!nodesLoaded || loadError) && (
        <section className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" role="status" data-testid="equipment-settings-inventory-warning">
          {i18n.t("sectorViews.L_inventaire_temps_reel_des_equipements_est_indisponible_Les_compteurs")}</section>
      )}

      <section className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <div className="rounded-xl border border-[var(--border-light)] bg-[var(--surface)] p-4">
          <p className="text-xs uppercase tracking-wide text-[var(--text-tertiary)]">{i18n.t("sectorViews.Equipements")}</p>
          <p className="mt-2 text-2xl font-semibold text-[var(--text-primary)]" data-testid="equipment-settings-count">{nodesLoaded ? nodes.length : '—'}</p>
        </div>
        <div className="rounded-xl border border-[var(--border-light)] bg-[var(--surface)] p-4">
          <p className="text-xs uppercase tracking-wide text-[var(--text-tertiary)]">{i18n.t("sectorViews.Sites_equipes")}</p>
          <p className="mt-2 text-2xl font-semibold text-[var(--text-primary)]" data-testid="equipment-settings-site-count">{sitesWithEquipment === null ? '—' : sitesWithEquipment}</p>
        </div>
        <div className="rounded-xl border border-[var(--border-light)] bg-[var(--surface)] p-4">
          <p className="text-xs uppercase tracking-wide text-[var(--text-tertiary)]">{i18n.t("online")}</p>
          <p className="mt-2 text-2xl font-semibold text-[var(--text-primary)]" data-testid="equipment-settings-online-count">{onlineCount === null ? '—' : onlineCount}</p>
        </div>
        <div className="rounded-xl border border-[var(--border-light)] bg-[var(--surface)] p-4">
          <p className="text-xs uppercase tracking-wide text-[var(--text-tertiary)]">{i18n.t("sectorViews.Non_recents")}</p>
          <p className="mt-2 text-2xl font-semibold text-[var(--text-primary)]" data-testid="equipment-settings-offline-count">{nonRecentCount === null ? '—' : nonRecentCount}</p>
        </div>
      </section>

      <section className="overflow-hidden rounded-2xl border border-[var(--border-light)] bg-[var(--surface)]">
        <div className="flex flex-col gap-3 border-b border-[var(--border-light)] px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
          <div>
            <h3 className="font-semibold text-[var(--text-primary)]">{i18n.t("sectorViews.Affectations_par_site")}</h3>
            <p className="mt-1 text-sm text-[var(--text-secondary)]">{i18n.t("sectorViews.Nom_metier_type_site_secteur_eventuel_et_fraicheur_des_remontees")}</p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/r6-soverdi-equipment')}
            data-testid="equipment-settings-open-operational"
            className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-xl border border-[var(--border-light)] px-3 py-2 text-sm font-semibold text-[var(--color-primary)] hover:bg-[var(--gray-100)]"
          >
            {i18n.t("sectorViews.Vue_Equipements")}<ExternalLink className="h-4 w-4" />
          </button>
        </div>

        {!nodesLoaded ? (
          <div className="px-4 py-10 text-center text-sm text-[var(--text-tertiary)]" data-testid="equipment-settings-inventory-unavailable">
            {i18n.t("sectorViews.Inventaire_equipements_indisponible_aucune_absence_d_equipement_n_est_")}</div>
        ) : (
          <div className="divide-y divide-[var(--border-light)]">
            {topology.sites.map((site) => {
              const siteNodes = nodes.filter((node) => nodeLocation.get(node.id)?.siteName === site.name);
              return (
                <div key={site.name} data-testid={`equipment-settings-site-${site.name}`}>
                  <div className="flex items-center justify-between bg-[var(--gray-50)] px-4 py-3 sm:px-5">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-[var(--color-primary)]" />
                      <span className="font-semibold text-[var(--text-primary)]">{site.name}</span>
                    </div>
                    <span className="text-xs font-medium text-[var(--text-secondary)]">{i18n.t("sectorViews.equipmentCount", { count: siteNodes.length })}</span>
                  </div>

                  {siteNodes.length === 0 ? (
                    <div className="px-4 py-5 text-sm text-[var(--text-tertiary)] sm:px-5">{i18n.t("sectorViews.Aucun_equipement_charge_pour_ce_site")}</div>
                  ) : (
                    <div className="divide-y divide-[var(--border-light)]">
                      {siteNodes.map((node) => {
                        const location = nodeLocation.get(node.id);
                        const online = isNodeOnline(node, Date.now());
                        const battery = getNodeBattery(node) ?? node.battery_v ?? undefined;
                        return (
                          <div key={node.id} className="grid gap-3 px-4 py-4 sm:px-5 lg:grid-cols-[1.2fr_.9fr_.9fr_.8fr] lg:items-center">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <Smartphone className="h-4 w-4 shrink-0 text-[var(--color-primary)]" />
                                <span className="truncate text-sm font-semibold text-[var(--text-primary)]">{node.name || i18n.t("sectorViews.Equipement")}</span>
                              </div>
                              <p className="mt-1 pl-6 text-xs text-[var(--text-tertiary)]">{equipmentType(node)}</p>
                            </div>
                            <div className="text-sm text-[var(--text-secondary)]">
                              <span className="font-medium text-[var(--text-primary)]">{site.name}</span>
                              <div className="mt-1 text-xs text-[var(--text-tertiary)]">{location?.sectorDisplayName || i18n.t("sectorViews.siteAssignment")}</div>
                            </div>
                            <div className="text-sm">
                              <div className={`inline-flex items-center gap-1.5 font-semibold ${online ? 'text-[var(--color-success)]' : 'text-[var(--text-tertiary)]'}`}>
                                <Radio className="h-3.5 w-3.5" /> {online ? i18n.t("online") : i18n.t("sectorViews.Non_recent")}
                              </div>
                              <div className="mt-1 text-xs text-[var(--text-tertiary)]">{ageText(nodeTimestamp(node))}</div>
                            </div>
                            <div className="text-sm lg:text-right">
                              {battery === undefined ? (
                                <span className="text-[var(--text-tertiary)]">{i18n.t("sectorViews.Batterie")}</span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-[var(--text-secondary)]"><Battery className="h-3.5 w-3.5" /> {formatEquipmentValue(battery, 1, 'V')}</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}

            {nodes.some((node) => !nodeLocation.has(node.id)) && (
              <div data-testid="equipment-settings-unassigned">
                <div className="bg-[var(--gray-50)] px-4 py-3 sm:px-5">
                  <span className="font-semibold text-[var(--text-primary)]">{i18n.t("sectorViews.Affectation_non_disponible")}</span>
                </div>
                <div className="space-y-3 px-4 py-4 sm:px-5">
                  {nodes.filter((node) => !nodeLocation.has(node.id)).map((node) => (
                    <div key={node.id} className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
                      <Smartphone className="h-4 w-4 text-[var(--text-tertiary)]" />
                      <span>{node.name || i18n.t("sectorViews.Equipement")}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      <section className="rounded-xl border border-[var(--border-light)] bg-[var(--gray-50)] p-4 text-sm text-[var(--text-secondary)]">
        {i18n.t("sectorViews.Les_modifications_d_affectation_identifiants_LoRaWAN_bindings_canaux_r")}</section>
    </div>
  );
};

export default SoverdiEquipmentSettingsR7;
